import React from 'react';
import {
  Box,
  ThemeProvider,
  CssBaseline,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Typography
} from '@mui/material';
import { styles, theme } from './TOCPreview.styles';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';
import { ETocSeparator, ITocStyles } from '@opengov/stories-shared/constants';
import { getFontFamily } from '../SetupPdfUtils';

interface TOCProps {
  data: IAccordionListNode[];
  level?: number;
  onStoryChange: (storyId: string) => void;
  tocStyles: ITocStyles;
}

const TOC: React.FC<TOCProps> = ({ data, level = 0, onStoryChange, tocStyles }) => {
  return (
    <List disablePadding sx={{ paddingLeft: level > 0 ? '24px' : '0px', ...styles.tocList }}>
      {data.map((item) => {
        const hasChildren = item.children && item.children.length > 0;

        return (
          <React.Fragment key={item.id}>
            {hasChildren ? (
              <>
                <ListItem sx={styles.sectionTitle}>
                  <Typography
                    variant={level === 0 ? 'h4' : 'bodyDefault'}
                    sx={{
                      marginTop: level === 0 ? '16px' : '0px',
                      ...styles.titleColor,
                      fontFamily: getFontFamily(tocStyles.fontType)
                    }}
                  >
                    {item.displayName}
                  </Typography>
                </ListItem>
                <TOC data={item.children || []} level={level + 1} onStoryChange={onStoryChange} tocStyles={tocStyles} />
              </>
            ) : (
              <ListItemButton
                sx={{
                  ...styles.storyTitle,
                  justifyContent: tocStyles.separator === ETocSeparator.Dotted ? 'flex-start' : 'space-between'
                }}
                onClick={() => onStoryChange(item.id)}
              >
                <Typography
                  component="p"
                  variant="bodyDefault"
                  sx={{ ...styles.storyTitleText, fontFamily: getFontFamily(tocStyles.fontType) }}
                >
                  {item.displayName}
                </Typography>
                {tocStyles.separator === ETocSeparator.Dotted && <Box sx={styles.separator} />}
                <Typography
                  variant="bodyDefault"
                  sx={{ ...styles.storyTitleText, fontFamily: getFontFamily(tocStyles.fontType) }}
                >
                  x
                </Typography>
              </ListItemButton>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default function TOCPreview({
  data,
  onStoryChange,
  messages,
  tocStyles
}: {
  data: IAccordionListNode[];
  onStoryChange: (storyId: string) => void;
  messages: ISetupPdfMessages;
  tocStyles: ITocStyles;
}) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Paper sx={styles.container}>
        <Box>
          <Typography variant="h1" sx={{ fontFamily: getFontFamily(tocStyles.fontType) }}>
            {messages.tableOfContents}
          </Typography>
          <Divider sx={styles.divider} />
        </Box>
        <Box sx={styles.tocContainer}>
          <TOC data={data} onStoryChange={onStoryChange} tocStyles={tocStyles} />
        </Box>
      </Paper>
    </ThemeProvider>
  );
}
